import React from 'react';
import { Tag, FlaskConical, Microscope, Clock, DollarSign, Archive, Rabbit } from 'lucide-react';

interface ExtractedItem {
  lab_code?: string;
  test_name?: string;
  method?: string;
  specimen?: string;
  turnaround_time?: string;
  cost?: number;
  storage_condition?: string;
}

interface ResultsTableProps {
  data?: {
    items?: ExtractedItem[];
  };
}

const ResultsTable: React.FC<ResultsTableProps> = ({ data }) => {
  if (!data || !data.items || data.items.length === 0) {
    return (
      <div className="text-center p-12 text-gray-400 bg-white rounded-3xl border-2 border-dashed border-pink-200 flex flex-col items-center">
        <Rabbit className="w-16 h-16 text-pink-300 mb-4 animate-bounce" />
        <p className="text-lg font-medium text-pink-400">No yummies yet! Please feed me a PDF file.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden shadow-xl shadow-pink-100/50 rounded-[2rem] border border-pink-50 bg-white">
      <table className="w-full text-sm text-left text-gray-600">
        <thead className="text-xs text-pink-700 uppercase bg-pink-100/80 border-b border-pink-200">
          <tr>
            <th scope="col" className="px-6 py-4 font-bold flex items-center gap-2">
              <Tag className="w-4 h-4" /> Lab Code
            </th>
            <th scope="col" className="px-6 py-4 font-bold">
              <span className="flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Test Name</span>
            </th>
            <th scope="col" className="px-6 py-4 font-bold">
              <span className="flex items-center gap-2"><Microscope className="w-4 h-4" /> Method</span>
            </th>
            <th scope="col" className="px-6 py-4 font-bold">Specimen</th>
            <th scope="col" className="px-6 py-4 font-bold">
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" /> TAT</span>
            </th>
            <th scope="col" className="px-6 py-4 font-bold">
              <span className="flex items-center gap-2"><DollarSign className="w-4 h-4" /> Cost</span>
            </th>
            <th scope="col" className="px-6 py-4 font-bold">
              <span className="flex items-center gap-2"><Archive className="w-4 h-4" /> Storage</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item, index) => (
            <tr key={index} className="bg-white border-b border-pink-50 hover:bg-pink-50/30 transition-colors">
              <td className="px-6 py-4 font-semibold text-gray-700 whitespace-nowrap">
                <span className="bg-pink-50 text-pink-600 px-2 py-1 rounded-lg font-mono text-xs border border-pink-100">
                  {item.lab_code}
                </span>
              </td>
              <td className="px-6 py-4 font-medium text-gray-800">
                {item.test_name}
              </td>
              <td className="px-6 py-4 text-xs text-gray-500">
                {item.method || '-'}
              </td>
              <td className="px-6 py-4 text-xs text-gray-500 max-w-xs truncate" title={item.specimen}>
                {item.specimen || '-'}
              </td>
              <td className="px-6 py-4 text-xs text-gray-500">
                {item.turnaround_time || '-'}
              </td>
              <td className="px-6 py-4 font-bold text-green-500 text-base">
                {item.cost !== undefined ? item.cost.toFixed(2) : 'N/A'}
              </td>
              <td className="px-6 py-4 text-xs text-gray-500">
                {item.storage_condition || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ResultsTable;
